import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '@/components/app';
import { VIDEO_LESSON_TYPE_LABELS } from '@/lib/labels';
import { VideoLessonDialogProps } from '../types';

type LessonType = 'practical' | 'theoritical' | 'other';
const TYPES: LessonType[] = ['practical', 'theoritical', 'other'];

export function VideoLessonDialog({
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
  isSubmitting = false,
}: VideoLessonDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 p-0">
        <DialogHeader bordered>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit();
          }}
        >
          <DialogBody>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Порядок" required hint="Меньше — выше в списке">
                <Input
                  type="number"
                  inputMode="numeric"
                  value={formData.sort_id}
                  onChange={(e) =>
                    onFormDataChange((prev) => ({
                      ...prev,
                      sort_id: Number.isNaN(Number(e.target.value)) ? 1 : Number(e.target.value),
                    }))
                  }
                />
              </FormField>
              <FormField label="Тип" required>
                {(p) => (
                  <Select
                    value={formData.type}
                    onValueChange={(value: LessonType) => onFormDataChange((prev) => ({ ...prev, type: value }))}
                  >
                    <SelectTrigger {...p}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {VIDEO_LESSON_TYPE_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FormField>
            </div>
            <FormField label="Название" required>
              <Input
                value={formData.title}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Название видеоурока"
              />
            </FormField>
            <FormField label="Ссылка на видео" required>
              <Input
                type="url"
                value={formData.url}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, url: e.target.value }))}
                placeholder="https://…"
              />
            </FormField>
            <FormField label="Описание" hint="Необязательно">
              <Textarea
                value={formData.description || ''}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, description: e.target.value }))}
                rows={4}
              />
            </FormField>
            <div className="flex items-start justify-between gap-4 rounded-xl bg-surface-alt px-3.5 py-3">
              <span className="flex flex-col gap-0.5">
                <label htmlFor="vl-active" className="text-[13px] font-semibold">
                  Показывать в приложении
                </label>
                <span id="vl-active-hint" className="text-xs text-text-2">
                  Выключенный урок остаётся здесь, но пользователи его не видят
                </span>
              </span>
              <Switch
                id="vl-active"
                aria-describedby="vl-active-hint"
                checked={formData.is_active ?? true}
                onCheckedChange={(checked) => onFormDataChange((prev) => ({ ...prev, is_active: checked }))}
              />
            </div>
          </DialogBody>
          <DialogFooter bordered>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Отмена
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin" aria-hidden />}
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
